// Approvals widget — the ASM's gates at a glance (Ship 2, Aug 25 2026).
// Queue head-counts: agreements awaiting first approval, canvasser
// appointments pending review, drone intake, and (Oct 27, 2026) the weekly
// SPIFFs waiting for approval. Read-only; acting happens on the ASM board.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";
import { fetchSpiffsWaitingCount } from "../../crew/crewQueries";

interface Counts {
  agreements: number;
  canvas: number;
  drone: number;
  spiffs: number;
}

export default function ApprovalsWidget() {
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [ag, cv, dr, sp] = await Promise.all([
        supabase.from("deals").select("id", { count: "exact", head: true }).eq("agreement_status", "pending_approval"),
        supabase.from("pending_canvas_appointments").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("deals").select("id", { count: "exact", head: true }).in("stage", ["pending_asm_review", "report_approved"]),
        fetchSpiffsWaitingCount().catch(() => 0),
      ]);
      if (cancelled) return;
      setCounts({ agreements: ag.count ?? 0, canvas: cv.count ?? 0, drone: dr.count ?? 0, spiffs: sp });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const row = (label: string, sub: string, n: number | undefined, to: string, key: string) => (
    <Link
      to={to}
      onClick={() => logCardClick(key)}
      className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-b-0"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-bardin-ink">{label}</div>
        <div className="text-[11px] text-slate-400">{sub}</div>
      </div>
      <span
        className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold ${
          (n ?? 0) > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-400"
        }`}
      >
        {n ?? "…"}
      </span>
    </Link>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🔨</span>
        <span className="font-bold text-sm text-bardin-ink">Approvals</span>
      </div>
      {row("Agreements", "awaiting first approval", counts?.agreements, "/asm", "widget_approvals_agreements")}
      {row("Canvas appointments", "pending your review", counts?.canvas, "/asm?tab=canvas", "widget_approvals_canvas")}
      {row("Drone intake", "new + report approved", counts?.drone, "/asm?tab=drone", "widget_approvals_drone")}
      {row("SPIFFs", "last week's, to approve Monday", counts?.spiffs, "/asm?tab=spiffs", "widget_approvals_spiffs")}
      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/asm"
          onClick={() => logCardClick("widget_approvals_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          ASM board ›
        </Link>
      </div>
    </div>
  );
}
